import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { CTA } from "./CTA";
import { SERVICES } from "@/data/services";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Solutions" },
  { to: "/about", label: "About" },
  { to: "/beyond", label: "Beyond" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  `relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:left-0 after:-bottom-0.5 after:h-px after:bg-[#D4AF37] after:transition-[width] after:duration-300 hover:after:w-full ${
    isActive ? "text-[#0D9488] after:w-full" : "text-[#0D9488] after:w-0"
  }`;

const DropItem = ({ s }) => (
  <Link to={`/services/${s.slug}`} data-testid={`dropdown-${s.slug}`} className="group grid grid-cols-[104px_1fr_auto] items-center gap-4 px-4 py-3.5 transition-colors duration-300 hover:bg-[#F4F9F8]">
    <span className="font-display text-xs font-bold tracking-[0.25em] text-[#0D9488]">{s.verb}</span>
    <span>
      <span className="font-display block text-sm font-semibold text-[#0D9488]">{s.title}</span>
      <span className="mt-0.5 block text-xs text-[#2D6A64]">{s.tagline}</span>
    </span>
    <span className="text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1">→</span>
  </Link>
);

const SolutionsMenu = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  return (
    <div className="py-7" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <NavLink to="/services" className={linkCls} data-testid="nav-solutions" aria-haspopup="true" aria-expanded={open}>
        <span className="inline-flex items-center gap-1.5">
          Solutions <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ x: "-50%" }}
            className="absolute left-1/2 top-full w-[min(620px,calc(100vw-48px))]"
            data-testid="solutions-dropdown"
          >
            <div className="border border-[#E2ECEB] border-t-[#D4AF37] bg-[#FDFFFF] p-3 shadow-[0_30px_60px_-30px_rgba(7,78,72,0.35)]">
              {SERVICES.map((s) => (
                <DropItem key={s.slug} s={s} />
              ))}
              <Link to="/services" data-testid="dropdown-all" className="font-display mt-2 flex items-center justify-between border-t border-[#E2ECEB] px-4 pb-2 pt-4 text-sm font-semibold text-[#0D9488] hover:text-[#074E48]">
                Explore Our Solutions <span className="text-[#D4AF37]">→</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#E2ECEB] bg-[#FDFFFF]/90 backdrop-blur-md" data-testid="site-header">
      <div className="relative mx-auto flex h-20 max-w-[1400px] items-center justify-between px-6 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-6 md:flex xl:gap-10" data-testid="desktop-nav">
          {LINKS.map((l) =>
            l.to === "/services" ? (
              <SolutionsMenu key={l.to} />
            ) : (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            )
          )}
          <div className="hidden lg:block">
            <CTA testId="nav-cta">Talk to Krimzon</CTA>
          </div>
        </nav>
        <button className="p-2 text-[#0D9488] md:hidden" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="max-h-[calc(100vh-80px)] overflow-y-auto border-t border-[#E2ECEB] bg-[#FDFFFF] px-6 pb-10 pt-6 md:hidden"
            data-lenis-prevent
            data-testid="mobile-menu"
          >
            <MobileLinks />
            <div className="mt-8">
              <CTA testId="mobile-nav-cta">Talk to Krimzon</CTA>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const M_LINK = "block border-b border-[#E2ECEB] py-4 font-display text-xl font-semibold text-[#0D9488]";

const MobileLinks = () => {
  const [sol, setSol] = useState(false);
  return LINKS.map((l) =>
    l.to === "/services" ? (
      <div key={l.to} className="border-b border-[#E2ECEB]">
        <button type="button" onClick={() => setSol((o) => !o)} aria-expanded={sol} data-testid="mobile-solutions-toggle" className="font-display flex w-full items-center justify-between py-4 text-xl font-semibold text-[#0D9488]">
          Solutions <ChevronDown size={20} className={`transition-transform duration-300 ${sol ? "rotate-180" : ""}`} />
        </button>
        {sol && (
          <div className="pb-3" data-testid="mobile-solutions-list">
            {SERVICES.map((s) => (
              <NavLink key={s.slug} to={`/services/${s.slug}`} className="font-display block py-3 pl-4 text-base text-[#2D6A64]">
                {s.verb} · {s.title}
              </NavLink>
            ))}
            <NavLink to="/services" end className="font-display block py-3 pl-4 text-base font-semibold text-[#0D9488]">Explore Our Solutions →</NavLink>
          </div>
        )}
      </div>
    ) : (
      <NavLink key={l.to} to={l.to} end={l.to === "/"} className={M_LINK} data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
        {l.label}
      </NavLink>
    )
  );
};
